/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AccountInfo,
  CambridgeOrganisation,
  EnvVariables,
  ISubscriptions,
} from 'src/types';
import { generateCodeChallenge } from 'src/utils/codeChallengeGenerator';
import envVariables from '../../env';
import { CUSTOM_LANG_PARAMS } from './constants';

const _gigyaPromise = () =>
  new Promise<void>((res) => {
    const check = () => {
      setTimeout(() => {
        if (typeof gigya === 'undefined') {
          check();
        } else {
          res();
        }
      }, 500);
    };
    check();
  });

export const waitForGigya = async (): Promise<void> => {
  await _gigyaPromise();
};

export const showLoginScreen = (
  onSuccess: (data: any) => void,
  onCancel?: (data: any) => void
): void => {
  gigya.accounts.showScreenSet({
    screenSet: 'CambridgeLogin-RegistrationLogin',
    startScreen: 'gigya-login-screen-standard',
    authFlow: 'redirect',
    customLang: CUSTOM_LANG_PARAMS,
    onHide: (data) => {
      if (
        ['cancel', 'canceled', 'fail'].includes(data.reason) ||
        data.errorCode
      ) {
        onCancel && onCancel(data);
      }
    },
    onAfterSubmit: (data) => {
      if (!data.response.errorCode) {
        onSuccess && onSuccess(data);
      }
    },
  });
};

export const getAccountInfo = (
  callback: (update: any) => void,
  include: string[] = []
): void => {
  gigya.accounts.getAccountInfo({
    callback,
    include: include.join(','),
  });
};

export const logout = (callback?: (update: any) => void): void => {
  gigya.accounts.logout({
    callback,
  });
};

export const changeOrganisation = async (
  organisation: CambridgeOrganisation,
  callback?: (update: any) => void
): Promise<void> => {
  await waitForGigya();
  let lastSelectedOrgNCN: string | undefined;
  if (organisation.sourceSystems && organisation.sourceSystems[0]) {
    lastSelectedOrgNCN = organisation.sourceSystems[0].orgId;
  }
  gigya.accounts.setAccountInfo({
    data: {
      lastSelectedOrg: organisation.bpid,
      lastSelectedOrgName: organisation.name,
      lastSelectedOrgNCN,
    },
    callback,
  });
};

export const updatePersonalInfo = async (
  firstName: string,
  lastName: string,
  country: string,
  callback?: (update: any) => void
): Promise<void> => {
  await waitForGigya();
  gigya.accounts.setAccountInfo({
    profile: { firstName, lastName, country },
    callback,
  });
};

export const changePassword = async (
  currentPassword: string,
  newPassword: string,
  callback?: (update: any) => void
): Promise<void> => {
  await waitForGigya();
  gigya.accounts.setAccountInfo({
    conflictHandling: 'fail',
    password: currentPassword,
    newPassword,
    callback,
  });
};

export const addEmail = async (
  email: string,
  onSuccess?: () => void,
  onError?: (error: any) => void
): Promise<void> => {
  const callback = (resp) => {
    resp.errorCode === 0
      ? verifyEmail(email, onSuccess, onError)
      : onError && onError(resp.errorMessage);
  };

  try {
    await waitForGigya();

    gigya.accounts.setAccountInfo({
      conflictHandling: 'fail',
      addLoginEmails: email,
      callback,
    });
  } catch (err) {
    onError && onError(err);
  }
};

export const removeEmail = async (
  email: string,
  onSuccess?: (update: any) => void,
  onError?: (error: any) => void
): Promise<void> => {
  const callback = (resp) => {
    resp.errorCode === 0
      ? onSuccess && onSuccess(resp)
      : onError && onError(resp.errorMessage);
  };

  try {
    await waitForGigya();

    gigya.accounts.setAccountInfo({
      removeLoginEmails: email,
      callback,
    });
  } catch (err) {
    onError && onError(err);
  }
};

export const verifyEmail = async (
  email: string,
  onSuccess?: () => void,
  onError?: (err: any) => void
): Promise<void> => {
  const callback = (resp) => {
    resp.errorCode === 0
      ? onSuccess && onSuccess()
      : onError && onError(resp.errorMessage);
  };

  try {
    await waitForGigya();
    gigya.accounts.resendVerificationCode({
      email,
      callback,
    });
  } catch (err) {
    onError && onError(err);
  }
};

export const changePrimaryEmail = async (
  email: string,
  onSuccess?: () => void,
  onError?: (err: any) => void
): Promise<void> => {
  const callback = (resp) => {
    resp.errorCode === 0
      ? onSuccess && onSuccess()
      : onError && onError(resp.errorMessage);
  };

  try {
    await waitForGigya();
    gigya.accounts.setAccountInfo({
      profile: { email },
      callback,
    });
  } catch (err) {
    onError && onError(err);
  }
};

export const openDelegateConsoleCallback = (accountInfo: AccountInfo): void => {
  const lastOrgSelected = accountInfo.data.lastSelectedOrg;
  const orgId = lastOrgSelected
    ? accountInfo.groups?.organizations?.find(
        (org) => org.bpid === lastOrgSelected
      ).orgId
    : null;

  if (orgId) gigya.accounts.b2b.openDelegatedAdminLogin({ orgId });
};

export const unauthorisedRedirect = async (
  envVars: EnvVariables
): Promise<any> => {
  const envMappings = {
    dev: 'dev',
    qa: 'qa',
    prep: 'pp',
    prod: 'live',
  };

  const env = envMappings[envVars.NODE_ENV] || envMappings.dev;

  const newNonce = Date.now();

  const code_challenge = await generateCodeChallenge();

  window.location.assign(
    (envVars.IS_CHINA
      ? 'https://fidm.cn1.sapcdm.cn/oidc/op/v1.0/'
      : `https://openid.${env}.sso.cambridge.org/oidc/op/v1.0/`) +
      envVars.API_KEY +
      '/authorize?client_id=' +
      envVars.CLIENT_ID +
      `&redirect_uri=${window.location.origin}/callback` +
      '&scope=openid+platformAccess%20roles%20profile%20email%20orgs' +
      '&code_challenge_method=S256' +
      `&code_challenge=${code_challenge}` +
      '&grant_type=authorization_code' +
      '&response_type=code' +
      '&nonce=' +
      newNonce +
      '&display=page' +
      '&state=' +
      window.location.pathname
  );
};

export const logoutRedirect = (): void => {
  sessionStorage.clear();
  logout((info) => {
    if (info.errorCode) {
      window.location.href = `${envVariables.PROXY_BASE_URL}/error`;
    } else {
      unauthorisedRedirect(envVariables);
    }
  });
};

export const clearCookies = (): void => {
  if (typeof window) {
    const cookies: string[] = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name] = cookie.trim().split('=');
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    }
  }
};

export const clearStorage = (): void => {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.clear();
    window.sessionStorage.clear();
  }
};

export const enableSocialProvider = (
  callback: (info: any) => void,
  title: string
): void => {
  gigya.socialize.addConnection({
    callback: callback,
    provider: title === 'google' ? 'googleplus' : title,
  });
};

export const disableSocialProvider = (
  callback: (info: any) => void,
  title: string
): void => {
  gigya.socialize.removeConnection({
    callback: callback,
    provider: title === 'google' ? 'googleplus' : title,
  });
};

export const setSubscriptions = (
  subscriptions: ISubscriptions,
  callback?: (event: any) => void
): void => {
  gigya.accounts.setAccountInfo({ subscriptions, callback });
};

export const getAccountSchema = (callback?: (response) => void): void => {
  gigya.accounts.getSchema({ callback });
};

export const getJWT = (): Promise<string> => {
  return new Promise((resolve) => {
    gigya.accounts.getJWT({
      callback: (response) => resolve(response.id_token),
      fields: 'data.claims.roles',
    });
  });
};

export const publishPhoto = (photo: string): Promise<any> => {
  return new Promise((resolve) => {
    gigya.accounts.setProfilePhoto({
      photoBytes: photo,
      publish: true,
      callback: (response) => resolve(response),
    });
  });
};
